import { useEffect, useState } from "react";
import { Lightning, FloppyDisk } from "@phosphor-icons/react";
import AppLayout from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api from "@/lib/api";
import { useWorkspace } from "@/context/WorkspaceContext";

const PRESETS = ["Professional", "Bold & punchy", "Warm & conversational", "Witty", "Inspirational", "Data-driven"];

export default function BrandVoice() {
  const { active, activeId, refresh } = useWorkspace();
  const [form, setForm] = useState({ tone: "", audience: "", style: "", description: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (active?.brand_voice) setForm(active.brand_voice);
  }, [active]);

  const save = async () => {
    if (!activeId) return;
    setSaving(true);
    try {
      await api.put(`/workspaces/${activeId}/brand-voice`, form);
      await refresh();
      toast.success("Brand voice saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <AppLayout>
      <div className="max-w-2xl">
        <div className="flex items-center gap-2 mb-2">
          <Lightning size={22} weight="fill" className="text-primary" />
          <span className="text-sm font-mono uppercase text-muted-foreground tracking-wide">{active?.name}</span>
        </div>
        <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tight">Brand Voice</h1>
        <p className="text-muted-foreground mt-2">Tune this once — every generated post will sound like you.</p>

        <div className="mt-8 rounded-lg border border-border bg-card p-6 md:p-8 space-y-6">
          <div>
            <Label htmlFor="tone">Tone</Label>
            <Input id="tone" data-testid="voice-tone" value={form.tone} onChange={set("tone")} className="mt-1.5" placeholder="e.g. Confident, warm, no fluff" />
            <div className="flex flex-wrap gap-2 mt-2">
              {PRESETS.map((p) => (
                <button key={p} data-testid={`tone-preset-${p.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  onClick={() => setForm((f) => ({ ...f, tone: p }))}
                  className="text-xs px-2.5 py-1 rounded-full border border-border hover:bg-accent transition-colors">{p}</button>
              ))}
            </div>
          </div>
          <div>
            <Label htmlFor="audience">Target audience</Label>
            <Input id="audience" data-testid="voice-audience" value={form.audience} onChange={set("audience")} className="mt-1.5" placeholder="e.g. Early-stage SaaS founders" />
          </div>
          <div>
            <Label htmlFor="style">Writing style</Label>
            <Input id="style" data-testid="voice-style" value={form.style} onChange={set("style")} className="mt-1.5" placeholder="e.g. Short sentences, punchy hooks, one CTA" />
          </div>
          <div>
            <Label htmlFor="desc">Extra notes (optional)</Label>
            <Textarea id="desc" data-testid="voice-description" value={form.description || ""} onChange={set("description")}
              className="mt-1.5 min-h-[100px]" placeholder="Words to avoid, signature phrases, hashtags you always use…" />
          </div>
          <Button data-testid="save-voice" onClick={save} disabled={saving} className="w-full sm:w-auto">
            <FloppyDisk size={18} className="mr-2" /> {saving ? "Saving…" : "Save brand voice"}
          </Button>
        </div>
      </div>
    </AppLayout>
  );
}
